import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm, NgModel } from '@angular/forms';
import { LookUpService } from '../../../services/lookupservice';
import { FeedstockService } from '../../../services/feedstockservice';
import { AttachDocumentsDialogData, DockTypesResponse } from '../../../interfaces/sitelockcodes';
import { DragDropModule } from '@angular/cdk/drag-drop';

interface StoredFile {
  name: string;
  type: string;
  data: string;
}

interface DocumentGroup {
  docTypeId: number;
  docTypeName: string;
  files: StoredFile[];
}

@Component({
  selector: 'app-attach-documents',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    DragDropModule
  ],
  templateUrl: './attach-documents.component.html',
  styleUrl: './attach-documents.component.scss'
})
export class AttachDocumentsComponent implements OnInit {
  
  @ViewChild('attachDocForm') attachDocForm!: NgForm;
  @ViewChild('docTypeInput') docTypeInput!: NgModel;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  dialogTitle: string = '';

  docTypes: DockTypesResponse[] = [];
  selectedFiles: File[] = []; // temp (before upload)
  documents: DocumentGroup[] = []; // saved data per doc type
  isUploadEnabled: boolean = false;
  screenTypeId: number = 0;
  loadScheduleId: number = 0;
  docTypeId : number | null = null;
  allowedExtensions = ['.jpg', '.jpeg', '.png', '.bmp', '.gif', '.pdf'];
  isDragOver = false;
  constructor(
    public dialogRef: MatDialogRef<AttachDocumentsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AttachDocumentsDialogData,
    private lookUpService: LookUpService,
    private feedstockService: FeedstockService
  ) {
    console.log("attachments", data);
      this.loadScheduleId = data.loadScheduleId;
      this.screenTypeId = data.screenTypeId;
      this.dialogTitle = data.name;
      const key = `documents_${data.loadScheduleId}`;
      const storedDocs = localStorage.getItem(key);

      if (storedDocs) {
        const parsed = JSON.parse(storedDocs);
        this.documents = Array.isArray(parsed) ? parsed : [];
      } else {
        this.documents = [];
      }
  }

  ngOnInit(): void {
    this.getFeedstockDocTypes();
  }

  getFeedstockDocTypes(): void {
    this.lookUpService.getDocTypeByScreenType(this.screenTypeId).subscribe({
      next: (data: DockTypesResponse[]) => {
        this.docTypes = data;
        this.docTypeId = null;
      }
    });
  }

  onDocTypeChange(): void {
    this.isUploadEnabled = !!this.docTypeId;

    if (!this.docTypeId) return;
}


  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    await this.processFiles(Array.from(input.files));

    input.value = '';
  }

  private async processFiles(files: File[]): Promise<void> {
    const loadSlipFilesControl = this.attachDocForm?.controls?.['loadSlipFiles'];

    if (!loadSlipFilesControl) {
      return;
    }

    if (this.docTypeInput?.control) {
      this.docTypeInput.control.setErrors(null);
    }

    if (files.length === 0) {
      this.selectedFiles = [];
      loadSlipFilesControl.setErrors({ required: true });
      loadSlipFilesControl.markAsTouched();
      return;
    }

    const invalidFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return !this.allowedExtensions.includes(ext);
    });

    if (invalidFiles.length > 0) {
      this.fileInput.nativeElement.value = '';
      this.selectedFiles = [];
      loadSlipFilesControl.setErrors({ invalidFileType: true });
      return;
    }

    this.selectedFiles = [
      ...this.selectedFiles,
      ...files
    ];

    const base64Files: StoredFile[] = [];

    const toBase64 = (file: File) => new Promise<StoredFile>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        const data = commaIndex > -1 ? result.substring(commaIndex + 1) : result;
        resolve({ name: file.name, type: file.type, data });
      };
      reader.onerror = err => reject(err);
      reader.readAsDataURL(file);
    });

    for (const file of files) {
      // eslint-disable-next-line no-await-in-loop
      base64Files.push(await toBase64(file));
    }

    const selectedDocType = this.docTypes.find(d => d.id === this.docTypeId);

    const docTypeName = selectedDocType?.docType || '';

    if (!Array.isArray(this.documents)) {
      this.documents = [];
    }

    const index = this.documents.findIndex(d => d.docTypeId === this.docTypeId);

    if (index > -1) {
      this.documents[index].files = [
        ...this.documents[index].files,
        ...base64Files.filter(newFile =>
          !this.documents[index].files.some(existing => existing.name === newFile.name)
        )
      ];
    } else {
      this.documents.push({
        docTypeId: this.docTypeId!,
        docTypeName,
        files: base64Files
      });
    }

    this.documents = [...this.documents];

    const key = `documents_${this.loadScheduleId}`;
    localStorage.setItem(key, JSON.stringify(this.documents));
  }

  onUploadDocument(): void {
    let isValid = true;

    if (!this.docTypeId) {
      this.docTypeInput.control.markAsTouched();
      this.docTypeInput.control.markAsDirty();
      this.docTypeInput.control.updateValueAndValidity();

      isValid = false;
    }

    if (this.selectedFiles.length === 0) {
      this.attachDocForm.controls['loadSlipFiles']
        ?.setErrors({ required: true });

      isValid = false;
    }

    if (!isValid) {
      return;
    }
    if (!this.documents || this.documents.length === 0) {
      return;
    }

    // Upload each file as an individual request so each file preserves its own docType
    // (some backends incorrectly apply a single docType to all files in a multi-file request)
    this.documents.forEach(doc => {
      doc.files.forEach(file => {
        const formData = new FormData();
        formData.append('loadScheduleId', this.loadScheduleId.toString());
        formData.append('docTypeId', (doc.docTypeId ?? 0).toString());
        formData.append('screenTypeId', this.screenTypeId.toString());

        const blob = this.base64ToBlob(file.data, file.type);
        formData.append('files', blob, file.name);

        this.feedstockService.uploadLoadDocument(formData).subscribe({
          next: () => {
            console.log(`Uploaded file ${file.name} for docType ${doc.docTypeId}`);
          }
        });
      });
    });

    const key = `documents_${this.loadScheduleId}`;
    localStorage.removeItem(key);
    this.documents = [];
    this.dialogRef.close(true);
  }

  removeFile(docTypeId: number, file: StoredFile): void {
    const idx = this.documents.findIndex(d => d.docTypeId === docTypeId);

    if (idx === -1) return;

    this.documents[idx].files = this.documents[idx].files.filter(f => f.name !== file.name);

    // remove group if no files
    if (this.documents[idx].files.length === 0) {
      this.documents.splice(idx, 1);
    }

    this.documents = [...this.documents];

    this.selectedFiles = this.selectedFiles.filter(sf => sf.name !== file.name);

    if (this.selectedFiles.length === 0) {
      this.fileInput.nativeElement.value = '';
      this.attachDocForm.controls['loadSlipFiles']
        ?.setErrors({ required: true });
    }
  }

  downloadFile(file: StoredFile): void {
    const blob = this.base64ToBlob(file.data, file.type);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;

    if (event.dataTransfer?.files?.length) {

      const files = Array.from(event.dataTransfer.files);

      // If you already have validation logic
      this.processFiles(files);

      // OR simply
      // this.selectedFiles = files;
    }
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  trackById(index: number, item: DockTypesResponse): number {
    return item.id;
  }
}
